import { rem } from 'polished'
import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sortByAll, type SortField } from 'lib/array/arrayUtils'
import getPropertyIcon from 'lib/offer/getPropertyIcon'

const FacilityGroups = styled.div`
  column-count: 1;
  column-gap: ${rem(32)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    column-count: 2;
  }

  ${mediaQueryUp.desktop} {
    column-count: 3;
  }
`

const FacilityGroup = styled.div`
  break-inside: avoid;
`

const GroupHeading = styled(BodyText)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} 0;
`

const Facilities = styled.ul`
  list-style-type: none;
  margin: ${rem(8)} 0 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const Facility = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(2)} 0;
  min-width: 0;
`

const UNORDERED_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER

const STRUCTURED_FACILITY_ITEM_SORT_FIELDS: Array<SortField<App.StructuredFacilityItem>> = [
  { selector: facility => facility.displayOrder ?? UNORDERED_DISPLAY_ORDER, direction: 'asc' },
  { selector: facility => facility.name, direction: 'asc' },
]

const STRUCTURED_FACILITY_GROUP_SORT_FIELDS: Array<SortField<App.StructuredFacilityGroup>> = [
  { selector: facilityGroup => facilityGroup.group.displayOrder ?? UNORDERED_DISPLAY_ORDER, direction: 'asc' },
  { selector: facilityGroup => facilityGroup.group.name, direction: 'asc' },
]

interface Props {
  showHeading?: boolean
  structuredFacilities: Array<App.StructuredFacilityGroup>
}

function PropertyStructuredFacilities({ showHeading = true, structuredFacilities }: Props) {
  const intl = useIntl()
  const sortedStructuredFacilities = useMemo(
    () => sortByAll(structuredFacilities.map(facilityGroup => ({
      ...facilityGroup,
      facilities: sortByAll(facilityGroup.facilities, STRUCTURED_FACILITY_ITEM_SORT_FIELDS),
    })), STRUCTURED_FACILITY_GROUP_SORT_FIELDS),
    [structuredFacilities],
  )

  const facilitiesTitle = intl.formatMessage({
    id: 'TdQ31JcW4K2rhT2wyPgV',
    defaultMessage: 'Facilities',
    description: 'Title for facilities section on single property page',
  })

  return <VerticalSpacer gap={12}>
    {showHeading && <Heading variant="heading4" as="h4">{facilitiesTitle}</Heading>}
    <FacilityGroups>
      {sortedStructuredFacilities.map(facilityGroup => {
        const GroupIcon = getPropertyIcon(facilityGroup.group.iconName)

        return <FacilityGroup key={facilityGroup.group.code}>
          <GroupHeading variant="medium" weight="bold">
            <GroupIcon size="S" />
            {facilityGroup.group.name}
          </GroupHeading>
          <Facilities>
            {facilityGroup.facilities.map(facility => (
              <Facility key={facility.code}>
                <LineCheckIcon size="S" />
                <BodyText variant="small">{facility.name}</BodyText>
              </Facility>
            ))}
          </Facilities>
        </FacilityGroup>
      })}
    </FacilityGroups>
  </VerticalSpacer>
}

export default memo(PropertyStructuredFacilities)
